import { Input, NumericTextBox } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { trashIcon, chevronUpIcon, chevronDownIcon } from '@progress/kendo-svg-icons';
import type { FieldEditorProps } from '../registry';
import type { FieldOption } from '../../types';
import { newId } from '../../utils/id';

/** Shared "Options" property-editor tab for choice-based field types (Radio, Dropdown, MultiSelect, Likert Scale). */
export function OptionsEditor({ field, onChange }: FieldEditorProps) {
  const options = field.options ?? [];

  function update(next: FieldOption[]) {
    onChange({ options: next.map((o, i) => ({ ...o, order: i })) });
  }

  function addOption() {
    const next: FieldOption = {
      id: newId(),
      value: `option-${options.length + 1}`,
      label: `Option ${options.length + 1}`,
      order: options.length,
    };
    update([...options, next]);
  }

  function patchOption(id: string, patch: Partial<FieldOption>) {
    update(options.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  }

  function removeOption(id: string) {
    update(options.filter((o) => o.id !== id));
  }

  function moveOption(id: string, dir: -1 | 1) {
    const idx = options.findIndex((o) => o.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= options.length) return;
    const next = [...options];
    [next[idx], next[target]] = [next[target], next[idx]];
    update(next);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--kendo-spacing-2)' }}>
      {options.map((opt, i) => (
        <div
          key={opt.id}
          style={{
            border: '1px solid var(--kendo-color-border)',
            borderRadius: 'var(--kendo-border-radius-md)',
            padding: 'var(--kendo-spacing-2)',
          }}
        >
          {/* Label gets its own full-width row — it's the longest, most important text
              (e.g. "More than half the days") and was unreadable squeezed into a shared
              row with everything else in a ~270px-wide panel. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--kendo-spacing-1)', marginBottom: 'var(--kendo-spacing-1)' }}>
            <span style={{ fontSize: 'var(--kendo-font-size-xs)', color: 'var(--kendo-color-subtle)', flexShrink: 0 }}>{i + 1}.</span>
            <Input
              value={opt.label}
              onChange={(e) => patchOption(opt.id, { label: String(e.value ?? '') })}
              placeholder="Label"
              style={{ flex: '1 1 0%', minWidth: 0 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--kendo-spacing-1)' }}>
            <Input
              value={opt.value}
              onChange={(e) => patchOption(opt.id, { value: String(e.value ?? '') })}
              placeholder="Value"
              style={{ flex: '1 1 0%', minWidth: 0 }}
            />
            <NumericTextBox
              value={opt.weight ?? null}
              onChange={(e) => patchOption(opt.id, { weight: e.value ?? undefined })}
              placeholder="Score"
              style={{ width: 56, flexShrink: 0 }}
            />
            <Button size="small" fillMode="flat" svgIcon={chevronUpIcon} onClick={() => moveOption(opt.id, -1)} title="Move up" />
            <Button size="small" fillMode="flat" svgIcon={chevronDownIcon} onClick={() => moveOption(opt.id, 1)} title="Move down" />
            <Button size="small" fillMode="flat" svgIcon={trashIcon} onClick={() => removeOption(opt.id)} title="Remove option" />
          </div>
        </div>
      ))}
      <Button fillMode="outline" onClick={addOption} style={{ alignSelf: 'flex-start' }}>
        + Add option
      </Button>
    </div>
  );
}
